import { type ReactNode, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

interface LayoutShellProps {
  children: ReactNode;
  sidebar?: ReactNode;
  topBar?: ReactNode;
  alertBanner?: ReactNode;
}

/**
 * The shared layout shell used by both portals.
 *
 * Structure (from docs/UI_CONVENTIONS.md):
 * ┌─────────────────────────────────────────────┐
 * │  Alert Banner Bar (sticky, full width)      │
 * ├────────┬────────────────────────────────────┤
 * │        │  Top Bar (breadcrumbs, user menu)  │
 * │  Side  ├────────────────────────────────────┤
 * │  bar   │                                    │
 * │        │  Content Area                      │
 * │  Nav   │  (page content renders here)       │
 * │        │                                    │
 * └────────┴────────────────────────────────────┘
 *
 * On mobile (< md) the sidebar is hidden and accessible via a hamburger button.
 * It slides in as a fixed drawer with a backdrop. Navigating to any route
 * automatically closes the drawer.
 */
export function LayoutShell({ children, sidebar, topBar, alertBanner }: LayoutShellProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { pathname } = useLocation();

  // Close the mobile drawer whenever the route changes.
  // pathname is the trigger, not a value consumed by the effect body.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional trigger-only dep
  useEffect(() => {
    setSidebarOpen(false);
  }, [pathname]);

  return (
    <div className="flex min-h-screen flex-col bg-bg-primary text-text-primary">
      {alertBanner}
      <div className="flex flex-1 overflow-hidden">
        {sidebar && (
          <>
            {/* Backdrop — mobile only, sits below the drawer */}
            {sidebarOpen && (
              <div
                className="fixed inset-0 z-30 bg-black/40 md:hidden"
                aria-hidden="true"
                onClick={() => setSidebarOpen(false)}
              />
            )}

            {/*
             * Single sidebar element — rendered once to avoid duplicate DOM IDs
             * and double-mounted component instances.
             * Mobile: fixed drawer that slides in/out via translate.
             * Desktop (md+): reverts to relative positioning inside the flex row,
             *   always visible regardless of sidebarOpen.
             */}
            <aside
              className={[
                'w-64 border-r border-border bg-bg-surface',
                'fixed inset-y-0 left-0 z-40 transition-transform duration-200 ease-in-out',
                sidebarOpen ? 'translate-x-0' : '-translate-x-full',
                'md:relative md:translate-x-0 md:transition-none md:shrink-0',
              ].join(' ')}
              aria-label="Navigation menu"
            >
              {sidebar}
            </aside>
          </>
        )}

        <div className="flex flex-1 flex-col overflow-hidden">
          {/*
           * Header row:
           * - On mobile: always shown when a sidebar exists (hamburger button lives here)
           * - On desktop: shown only when topBar content is provided; if no topBar the
           *   mobile-only header is hidden via md:hidden so desktop gets no wasted bar.
           */}
          {(topBar || sidebar) && (
            <header
              className={[
                'flex items-center gap-3 border-b border-border bg-bg-primary px-4 py-3',
                topBar ? 'md:px-6' : 'md:hidden',
              ].join(' ')}
            >
              {sidebar && (
                <button
                  type="button"
                  aria-label="Open navigation menu"
                  aria-expanded={sidebarOpen}
                  onClick={() => setSidebarOpen(true)}
                  className="shrink-0 rounded-lg p-1.5 text-text-secondary hover:bg-bg-elevated hover:text-text-primary focus:outline-none focus:ring-2 focus:ring-accent md:hidden"
                >
                  <svg
                    className="h-5 w-5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M3 6h18M3 12h18M3 18h18" />
                  </svg>
                </button>
              )}
              {topBar}
            </header>
          )}

          <main className="flex-1 overflow-auto p-6">{children}</main>
        </div>
      </div>
    </div>
  );
}
